// v4 moose — REDESIGNED. A towering bull moose: tall stockinged quad legs, a
// deep-chested body sloping to lower hindquarters with a shoulder hump, a
// thick neck and long Roman-nosed head with a drooping bulbous muzzle and a
// dewlap bell under the throat, wide mule ears, and the broad palmate antlers
// with rim tines and brow tines. Two coats: dark and lighter brown.
//
// Ported 1:1 from age-of-mcp's v4/moose.ts, using the library's shared
// `wetland.quadLegs`/`wetland.eyes` helpers — identical to the v4 source's.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const { quadLegs, eyes } = wetland;

const COATS = [
  { body: "#3a2a1c", hind: "#332416", hump: "#443120", neck: "#3e2d1e",
    head: "#46341f", muzzle: "#2a2018", leg: "#4a3826", stocking: "#9a8a72" },
  { body: "#54402a", hind: "#4a3824", hump: "#5e4a30", neck: "#58442c",
    head: "#624c30", muzzle: "#3a2c20", leg: "#5e4a32", stocking: "#aa9a80" },
];

function partsFor(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = quadLegs(0.085, 0.042, 0.25, 0.0105, c.leg,
    { lowerH: 0.09, lowerColor: c.stocking, rFoot: 0.008 });
  parts.push(
    // deep-chested body sloping to lower hindquarters
    { geometry: new THREE.IcosahedronGeometry(0.068, 0), color: c.body,
      position: [0.01, 0.25, 0], scale: [1.65, 1.05, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: c.hind,
      position: [-0.085, 0.245, 0], scale: [1.0, 0.95, 0.85] },
    // the shoulder hump
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.hump,
      position: [0.06, 0.3, 0], scale: [1.0, 0.9, 0.8] },
    // thick neck and the long Roman-nosed head
    { geometry: new THREE.CylinderGeometry(0.024, 0.034, 0.085, 5), color: c.neck,
      position: [0.125, 0.295, 0], rotation: [0, 0, -0.6] },
    { geometry: new THREE.IcosahedronGeometry(0.028, 0), color: c.head,
      position: [0.175, 0.325, 0], scale: [1.6, 0.85, 0.75] },
    // drooping bulbous muzzle
    { geometry: new THREE.ConeGeometry(0.017, 0.045, 4), color: c.head,
      position: [0.215, 0.305, 0], rotation: [0, 0, -2.05] },
    { geometry: new THREE.IcosahedronGeometry(0.0125, 0), color: c.muzzle,
      position: [0.234, 0.292, 0], scale: [1.1, 0.9, 0.9] },
    // the dewlap bell under the throat
    { geometry: new THREE.ConeGeometry(0.0095, 0.05, 4), color: c.neck,
      position: [0.155, 0.27, 0], rotation: [0, 0, 3.3] },
    ...eyes(0.178, 0.336, 0.021, 0.0042),
  );
  for (const s of [1, -1]) {
    parts.push(
      // wide mule ears
      { geometry: new THREE.ConeGeometry(0.009, 0.03, 4), color: c.head,
        position: [0.155, 0.355, s * 0.026], rotation: [s * 1.1, 0, 0.2] },
      // palmate antler: out-swept beam, broad cupped palm, rim tines —
      // seated low on the skull (a thicker, longer beam, dropped down) so
      // the rack reads as growing from the head, not floating above it.
      { geometry: new THREE.CylinderGeometry(0.008, 0.010, 0.044, 4),
        color: "#cdb184", position: [0.148, 0.348, s * 0.03],
        rotation: [s * 1.25, 0, 0] },
      { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: "#d8c194",
        position: [0.148, 0.370, s * 0.062], rotation: [-s * 0.35, 0, 0],
        scale: [1.35, 0.3, 1.05] },
      { geometry: new THREE.ConeGeometry(0.004, 0.022, 4), color: "#d8c194",
        position: [0.122, 0.390, s * 0.075], rotation: [-s * 0.2, 0, 0] },
      { geometry: new THREE.ConeGeometry(0.004, 0.022, 4), color: "#d8c194",
        position: [0.148, 0.392, s * 0.078], rotation: [-s * 0.2, 0, 0] },
      { geometry: new THREE.ConeGeometry(0.004, 0.022, 4), color: "#d8c194",
        position: [0.174, 0.390, s * 0.075], rotation: [-s * 0.2, 0, 0] },
      // brow tine over the face
      { geometry: new THREE.ConeGeometry(0.0045, 0.025, 4), color: "#cdb184",
        position: [0.185, 0.375, s * 0.045], rotation: [s * 0.45, 0, -0.5] },
    );
  }
  return parts;
}

export const mooseV4 = defineModel({
  id: "moose-v4",
  scale: 2.03, // real-world size correction (see Model.scale)
  batchId: "polished",
  name: "Moose (v4)",
  tags: ["creature","moose","low-poly","age-of-mcp","fable"],
  summary: "Low-poly moose.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.18,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
  ],
  animations: [],
});
